import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Calendar, CheckCircle2, XCircle } from "lucide-react";

const FILTERS = [
  ["", "All"],
  ["pending", "Pending"],
  ["approved", "Approved"],
  ["denied", "Denied"],
];

export default function AdminLeave() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = () => {
    const q = filter ? `?status=${filter}` : "";
    api.get(`/leave-requests${q}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const decide = async (l, status) => {
    const note = status === "denied" ? prompt("Reason for denial (optional)?") || "" : "";
    try {
      await api.patch(`/leave-requests/${l.leave_id}`, { status, note });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  return (
    <div data-testid="admin-leave">
      <p className="overline">HR</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-6">Leave requests</h1>

      <div className="flex gap-2 mb-4">
        {FILTERS.map(([v, l]) => (
          <button
            key={v || "all"}
            onClick={() => setFilter(v)}
            data-testid={`filter-${v || "all"}`}
            className={`text-xs px-3 py-1.5 rounded-full transition ${
              filter === v ? "bg-[#0A2540] text-white" : "bg-slate-100 dark:bg-slate-800 text-[#475569]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-[#94A3B8] italic py-8 text-center">No requests match this filter.</p>
      ) : (
        <div className="card-pb overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Employee</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Dates</th>
                <th className="px-3 py-2 text-right">Days</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.leave_id} className="border-b border-[#E2E8F0] dark:border-slate-700" data-testid={`row-${l.leave_id}`}>
                  <td className="px-3 py-2 font-mono text-[11px] text-[#94A3B8]">{l.leave_number}</td>
                  <td className="px-3 py-2 text-[#0A2540] dark:text-white">{l.user_name}</td>
                  <td className="px-3 py-2 capitalize">{l.leave_type}</td>
                  <td className="px-3 py-2 text-xs text-[#475569] dark:text-slate-400">{l.start_date} → {l.end_date}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{l.days}</td>
                  <td className="px-3 py-2 max-w-xs truncate text-xs">{l.reason || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                      l.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                      l.status === "denied" ? "bg-red-100 text-red-700" :
                      l.status === "cancelled" ? "bg-slate-200 text-slate-600" :
                      "bg-amber-100 text-amber-700"
                    }`}>{l.status}</span>
                  </td>
                  <td className="px-3 py-2">
                    {l.status === "pending" && (
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => decide(l, "approved")} data-testid={`approve-${l.leave_id}`}
                                className="text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1 text-xs">
                          <CheckCircle2 className="w-4 h-4" /> Approve
                        </button>
                        <button onClick={() => decide(l, "denied")} data-testid={`deny-${l.leave_id}`}
                                className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-xs">
                          <XCircle className="w-4 h-4" /> Deny
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
